/**
 * EEQUOTE — QuoteModule
 * Self-contained, configurable multi-step quote request form.
 * Usage: QuoteModule.init('container-id', config)
 */
(function (global) {
  'use strict';

  // ─── SVG icon helpers ────────────────────────────────────────────────────
  const icons = {
    check: `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="3,8 6.5,12 13,4"/></svg>`,
    chevRight: `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6,3 11,8 6,13"/></svg>`,
    chevLeft: `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="10,3 5,8 10,13"/></svg>`,
    exclaim: `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="8" cy="8" r="6"/><line x1="8" y1="5" x2="8" y2="8.5"/><circle cx="8" cy="11" r="0.5" fill="currentColor" stroke="none"/></svg>`,
    upload: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="16 16 12 12 8 16"/><line x1="12" y1="12" x2="12" y2="21"/><path d="M20.39 18.39A5 5 0 0018 9h-1.26A8 8 0 103 16.3"/></svg>`,
    file: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>`,
    trash: `<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="2,4 14,4"/><path d="M5 4V2h6v2"/><path d="M6 7v5M10 7v5"/><rect x="3" y="4" width="10" height="10" rx="1"/></svg>`,
    user: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`,
    grid: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>`,
    sliders: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>`,
    paperclip: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>`,
    star: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
  };

  // ─── Defaults ────────────────────────────────────────────────────────────
  const DEFAULTS = {
    title: 'Request a Quote',
    accentColor: null,
    steps: {
      contact: { enabled: true, heading: 'Your Details', fields: {
        firstName: { label: 'First Name', required: true },
        lastName:  { label: 'Last Name',  required: true },
        email:     { label: 'Email Address', required: true },
        phone:     { label: 'Phone Number',  required: false },
        deadline:  { label: 'Need by Date',  required: false },
        quantity:  { label: 'Quantity',      required: true },
      }},
      product: { enabled: true, heading: 'Choose Product Type', label: 'Product', required: true, multiSelect: false, options: [] },
      specs: {
        enabled: true, heading: 'Specifications',
        calculator: { enabled: true, defaultUnit: 'cm' },
        dropdowns: [],
      },
      upload: { enabled: true, heading: 'Files & Notes', maxSizeMB: 10, maxFiles: 5,
        accept: 'image/*,.pdf,.eps,.ai,.svg,.zip',
        acceptLabel: 'JPG, PNG, PDF, EPS, AI, ZIP — max 10 MB each',
        notesLabel: 'Other Notes', notesPlaceholder: 'Any additional details…',
      },
    },
    // Must throw, never log. If quote.config.js fails to load but init() still
    // runs, a logging default tells the customer "Submitted!" while the quote
    // goes to the console and nowhere else - a lost lead that looks like a win.
    onSubmit: async () => {
      throw new Error('Quote form is not configured. Please call us on 020 3432 5550.');
    },
  };

  // ─── QuoteModule class ───────────────────────────────────────────────────
  class QuoteModule {
    constructor(containerId, userConfig) {
      this.container = typeof containerId === 'string'
        ? document.getElementById(containerId)
        : containerId;

      if (!this.container) throw new Error(`[EEQUOTE] Container "${containerId}" not found.`);

      this.cfg = this._merge(DEFAULTS, userConfig || {});
      this.currentStep = 1;
      this.uploadedFiles = [];
      this.selectedProducts = [];
      this._turnstileToken = null;
      this._turnstileWidgetId = null;
      this._captchaReason = null;   // why no token, for the server to record
      this.currentUnit = this.cfg.steps.specs.calculator.defaultUnit || 'cm';
      this._buildStepMap();
      this._render();
      this._attachEvents();
      this._updateProgress(1, null);
    }

    // ── Static factory ─────────────────────────────────────────────────────
    static init(containerId, config) {
      return new QuoteModule(containerId, config);
    }

    // ── Build ordered list of enabled steps ───────────────────────────────
    _buildStepMap() {
      const s = this.cfg.steps;
      this._steps = [];
      if (s.contact?.enabled !== false) this._steps.push({ key: 'contact', num: 0, label: 'Contact', icon: 'user' });
      if (s.product?.enabled !== false) this._steps.push({ key: 'product', num: 0, label: 'Product', icon: 'grid' });
      if (s.specs?.enabled   !== false) this._steps.push({ key: 'specs',   num: 0, label: 'Details', icon: 'sliders' });
      if (s.upload?.enabled  !== false) this._steps.push({ key: 'upload',  num: 0, label: 'Files',   icon: 'paperclip' });
      this._steps.push({ key: 'review', num: 0, label: 'Review', icon: 'star' });
      this._steps.forEach((s, i) => s.num = i + 1);
      this._totalSteps = this._steps.length;
    }

    // ── Merge configs (shallow-merge nested objects) ───────────────────────
    _merge(base, override) {
      const result = Object.assign({}, base);
      for (const key of Object.keys(override)) {
        if (override[key] && typeof override[key] === 'object' && !Array.isArray(override[key]) && typeof base[key] === 'object') {
          result[key] = this._merge(base[key], override[key]);
        } else {
          result[key] = override[key];
        }
      }
      return result;
    }

    // ── Render ─────────────────────────────────────────────────────────────
    _render() {
      const cfg = this.cfg;
      if (cfg.accentColor) {
        this.container.style.setProperty('--qq-accent', cfg.accentColor);
      }

      this.container.classList.add('qq-wrapper');
      this.container.innerHTML = `
        <div class="qq-card">
          <div class="qq-card-header">
            <div class="qq-card-title">${this._esc(cfg.title)}</div>
            <div class="qq-card-subtitle">Fill in the form below and we'll get back to you shortly.</div>
          </div>

          <div class="qq-progress" role="list" aria-label="Form progress">
            ${this._renderProgress()}
          </div>

          <div class="qq-steps-container" id="qq-steps-container">
            ${this._renderStepContact()}
            ${this._renderStepProduct()}
            ${this._renderStepSpecs()}
            ${this._renderStepUpload()}
            ${this._renderStepReview()}
          </div>

          <div class="qq-error-banner" id="qq-error-banner" role="alert">
            ${icons.exclaim}
            <span id="qq-error-text">Something went wrong. Please try again.</span>
          </div>

          <div class="qq-nav" id="qq-nav">
            <button class="qq-btn qq-btn--ghost" id="qq-btn-back" type="button">
              ${icons.chevLeft} <span class="qq-btn-label">Back</span>
            </button>
            <button class="qq-btn qq-btn--primary" id="qq-btn-next" type="button">
              <span class="qq-btn-label">Next</span> ${icons.chevRight}
            </button>
            <button class="qq-btn qq-btn--primary qq-btn--submit" id="qq-btn-submit" type="button" style="display:none">
              <div class="qq-spinner"></div>
              <span class="qq-btn-label">Submit Quote</span>
              <span class="qq-btn-ok" style="display:none">${icons.check}</span>
            </button>
          </div>

          <div class="qq-success-screen" id="qq-success-screen">
            <div class="qq-success-icon">${icons.check}</div>
            <div class="qq-success-title">Quote Submitted!</div>
            <p class="qq-success-msg">Thank you — we've received your request and will be in touch within 24 hours.</p>
          </div>

          <div aria-live="polite" class="qq-sr-live" id="qq-live"></div>
        </div>
      `;

      this._showStep(1, null);
    }

    _renderProgress() {
      return this._steps.map((step, i) => {
        const isLast = i === this._steps.length - 1;
        return `
          <div class="qq-progress-step" id="qq-prog-${step.num}" role="listitem" aria-label="Step ${step.num} of ${this._totalSteps}: ${step.label}">
            <div class="qq-progress-dot" aria-hidden="true">
              <span class="qq-dot-num">${step.num}</span>
              ${icons.check}
            </div>
            <span class="qq-progress-label">${step.label}</span>
          </div>
          ${!isLast ? `<div class="qq-progress-line"><div class="qq-progress-line-fill" id="qq-line-${step.num}"></div></div>` : ''}
        `;
      }).join('');
    }

    _renderStepContact() {
      const s = this.cfg.steps.contact;
      if (!s || s.enabled === false) return '';
      const f = s.fields || {};
      const stepNum = this._stepNum('contact');
      return `
        <div class="qq-step" id="qq-step-${stepNum}" data-step="${stepNum}" role="group" aria-labelledby="qq-heading-${stepNum}">
          <h2 class="qq-step-heading" id="qq-heading-${stepNum}">
            <span class="qq-step-heading-icon">${icons.user}</span>
            ${this._esc(s.heading || 'Your Details')}
          </h2>
          <div class="qq-grid">
            ${this._renderField('firstName', f.firstName || { label:'First Name', required:true })}
            ${this._renderField('lastName',  f.lastName  || { label:'Last Name',  required:true })}
            ${this._renderField('email',     f.email     || { label:'Email Address', required:true }, 'email')}
            ${this._renderField('phone',     f.phone     || { label:'Phone Number' },  'tel')}
            ${this._renderField('deadline',  f.deadline  || { label:'Need by Date' },  'date')}
            ${this._renderField('quantity',  f.quantity  || { label:'Quantity', required:true }, 'number')}
          </div>
          <!-- Honeypot: off-screen, never display:none. Real people never see or fill it. -->
          <div class="qq-hp" aria-hidden="true"><input id="qq-hp" name="company_website" type="text" tabindex="-1" autocomplete="off"></div>
        </div>`;
    }

    _renderField(name, cfg, type = 'text') {
      const req = cfg.required ? `<span class="qq-required" aria-hidden="true">*</span>` : '';
      const ariaReq = cfg.required ? 'aria-required="true"' : '';
      return `
        <div class="qq-field" id="qq-field-${name}">
          <label class="qq-label" for="qq-${name}">${this._esc(cfg.label)}${req}</label>
          <input class="qq-input" id="qq-${name}" name="${name}" type="${type}" placeholder="${this._esc(cfg.label)}" ${ariaReq} autocomplete="${this._autoComplete(name)}">
          <span class="qq-field-error" id="qq-err-${name}" role="alert">${icons.exclaim} <span></span></span>
        </div>`;
    }

    _renderStepProduct() {
      const s = this.cfg.steps.product;
      if (!s || s.enabled === false) return '';
      const stepNum = this._stepNum('product');
      const cards = (s.options || []).map(opt => `
        <div class="qq-option-card" tabindex="0" role="${s.multiSelect ? 'checkbox' : 'radio'}" aria-checked="false"
             data-product-id="${this._esc(opt.id)}" aria-label="${this._esc(opt.label)}">
          <img class="qq-option-card-img" src="${this._esc(opt.image || '')}" alt="${this._esc(opt.label)}"
               onerror="this.style.opacity='0.3'">
          <div class="qq-option-card-body">
            <div class="qq-option-card-label">${this._esc(opt.label)}</div>
            ${opt.description ? `<div class="qq-option-card-desc">${this._esc(opt.description)}</div>` : ''}
          </div>
          <div class="qq-option-card-check" aria-hidden="true">${icons.check}</div>
        </div>`).join('');

      return `
        <div class="qq-step" id="qq-step-${stepNum}" data-step="${stepNum}" role="group" aria-labelledby="qq-heading-${stepNum}">
          <h2 class="qq-step-heading" id="qq-heading-${stepNum}">
            <span class="qq-step-heading-icon">${icons.grid}</span>
            ${this._esc(s.heading || 'Product Type')}
          </h2>
          <div class="qq-cards-grid" id="qq-cards-grid" role="${s.multiSelect ? 'group' : 'radiogroup'}" aria-label="Product options">
            ${cards || '<p style="color:var(--qq-text-muted);font-size:1.36rem">No product options configured.</p>'}
          </div>
          <span class="qq-field-error" id="qq-err-product" role="alert" style="margin-top:8px">${icons.exclaim} <span></span></span>
        </div>`;
    }

    _renderStepSpecs() {
      const s = this.cfg.steps.specs;
      if (!s || s.enabled === false) return '';
      const stepNum = this._stepNum('specs');
      const calc = s.calculator;
      const calcHtml = calc && calc.enabled !== false ? `
        <div class="qq-calc-section">
          <div class="qq-calc-label">${this._esc(calc.label || 'Dimensions')}</div>
          <div class="qq-unit-toggle" role="group" aria-label="Unit selection">
            <button class="qq-unit-btn ${this.currentUnit === 'cm' ? 'qq-active' : ''}" data-unit="cm" type="button" aria-pressed="${this.currentUnit === 'cm'}">cm</button>
            <button class="qq-unit-btn ${this.currentUnit === 'in' ? 'qq-active' : ''}" data-unit="in" type="button" aria-pressed="${this.currentUnit === 'in'}">inches</button>
          </div>
          <div class="qq-calc-inputs">
            <div class="qq-calc-field">
              <label for="qq-width">Width</label>
              <div class="qq-calc-input-wrap">
                <input class="qq-input" id="qq-width" name="width" type="number" min="0" step="0.1" placeholder="0" inputmode="decimal">
                <span class="qq-unit-suffix" id="qq-unit-w">${this.currentUnit}</span>
              </div>
              <span class="qq-field-error" id="qq-err-width" role="alert">${icons.exclaim} <span></span></span>
            </div>
            <div class="qq-calc-field">
              <label for="qq-height">Height</label>
              <div class="qq-calc-input-wrap">
                <input class="qq-input" id="qq-height" name="height" type="number" min="0" step="0.1" placeholder="0" inputmode="decimal">
                <span class="qq-unit-suffix" id="qq-unit-h">${this.currentUnit}</span>
              </div>
              <span class="qq-field-error" id="qq-err-height" role="alert">${icons.exclaim} <span></span></span>
            </div>
          </div>
          <div class="qq-size-display" aria-live="polite" aria-label="Computed size">
            <div class="qq-size-display-label">Your Size</div>
            <div class="qq-size-display-value" id="qq-size-val">0.00 × 0.00</div>
            <div class="qq-size-display-unit" id="qq-size-unit">${this.currentUnit}</div>
          </div>
        </div>` : '';

      const dropdowns = (s.dropdowns || []).map(dd => {
        const req = dd.required ? `<span class="qq-required" aria-hidden="true">*</span>` : '';
        const ariaReq = dd.required ? 'aria-required="true"' : '';
        const opts = (dd.options || []).map(o => `<option value="${this._esc(o)}">${this._esc(o)}</option>`).join('');
        return `
          <div class="qq-field" id="qq-field-dd-${dd.id}">
            <label class="qq-label" for="qq-dd-${dd.id}">${this._esc(dd.label)}${req}</label>
            <select class="qq-select" id="qq-dd-${dd.id}" name="${dd.id}" ${ariaReq}>${opts}</select>
            <span class="qq-field-error" id="qq-err-dd-${dd.id}" role="alert">${icons.exclaim} <span></span></span>
          </div>`;
      }).join('');

      return `
        <div class="qq-step" id="qq-step-${stepNum}" data-step="${stepNum}" role="group" aria-labelledby="qq-heading-${stepNum}">
          <h2 class="qq-step-heading" id="qq-heading-${stepNum}">
            <span class="qq-step-heading-icon">${icons.sliders}</span>
            ${this._esc(s.heading || 'Specifications')}
          </h2>
          ${calcHtml}
          <div class="qq-dropdowns-grid">${dropdowns}</div>
        </div>`;
    }

    _renderStepUpload() {
      const s = this.cfg.steps.upload;
      if (!s || s.enabled === false) return '';
      const stepNum = this._stepNum('upload');
      return `
        <div class="qq-step" id="qq-step-${stepNum}" data-step="${stepNum}" role="group" aria-labelledby="qq-heading-${stepNum}">
          <h2 class="qq-step-heading" id="qq-heading-${stepNum}">
            <span class="qq-step-heading-icon">${icons.paperclip}</span>
            ${this._esc(s.heading || 'Files & Notes')}
          </h2>
          <div class="qq-upload-zone" id="qq-upload-zone" role="button" tabindex="0" aria-label="Upload files — drag and drop or click to browse">
            <input type="file" id="qq-file-input" accept="${this._esc(s.accept || '*')}" multiple aria-label="File upload" tabindex="-1">
            <div class="qq-upload-icon">${icons.upload}</div>
            <div class="qq-upload-text">Drop files here or <span>browse</span></div>
            <div class="qq-upload-hint">${this._esc(s.acceptLabel || '')}</div>
          </div>
          <div class="qq-file-list" id="qq-file-list"></div>
          <div class="qq-field">
            <label class="qq-label" for="qq-notes">${this._esc(s.notesLabel || 'Other Notes')}</label>
            <textarea class="qq-textarea" id="qq-notes" name="notes" placeholder="${this._esc(s.notesPlaceholder || '')}" rows="4"></textarea>
          </div>
        </div>`;
    }

    _renderStepReview() {
      const stepNum = this._stepNum('review');
      return `
        <div class="qq-step" id="qq-step-${stepNum}" data-step="${stepNum}" role="group" aria-labelledby="qq-heading-${stepNum}">
          <h2 class="qq-step-heading" id="qq-heading-${stepNum}">
            <span class="qq-step-heading-icon">${icons.star}</span>
            Review Your Quote
          </h2>
          <div id="qq-review-content"></div>
          <div id="qq-captcha-wrap" class="qq-captcha-wrap"></div>
        </div>`;
    }

    // ── Events ─────────────────────────────────────────────────────────────
    _attachEvents() {
      const q = id => this.container.querySelector(id);

      q('#qq-btn-next').addEventListener('click', () => this._advance());
      q('#qq-btn-back').addEventListener('click', () => this._retreat());
      q('#qq-btn-submit').addEventListener('click', () => this._submit());

      // Product card selection
      this.container.addEventListener('click', e => {
        const card = e.target.closest('.qq-option-card');
        if (!card) return;
        this._selectCard(card);
      });
      this.container.addEventListener('keydown', e => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const card = e.target.closest('.qq-option-card');
        if (!card) return;
        e.preventDefault();
        this._selectCard(card);
      });

      // Unit toggle
      this.container.addEventListener('click', e => {
        const btn = e.target.closest('.qq-unit-btn');
        if (!btn) return;
        this._setUnit(btn.dataset.unit);
      });

      // Live size calculation
      ['qq-width', 'qq-height'].forEach(id => {
        const el = q(`#${id}`);
        if (el) el.addEventListener('input', () => this._computeSize());
      });

      // Upload zone
      const zone = q('#qq-upload-zone');
      if (zone) {
        zone.addEventListener('dragover', e => { e.preventDefault(); zone.classList.add('qq-drag-over'); });
        zone.addEventListener('dragleave', () => zone.classList.remove('qq-drag-over'));
        zone.addEventListener('drop', e => { e.preventDefault(); zone.classList.remove('qq-drag-over'); this._handleFiles(e.dataTransfer.files); });
        zone.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); q('#qq-file-input').click(); } });
      }

      const fileInput = q('#qq-file-input');
      if (fileInput) fileInput.addEventListener('change', () => { this._handleFiles(fileInput.files); fileInput.value = ''; });

      // Clear error on input
      this.container.addEventListener('input', e => {
        if (e.target.matches('.qq-input, .qq-select, .qq-textarea')) {
          this._clearFieldError(e.target.name || e.target.id.replace('qq-', ''));
        }
      });
    }

    // ── Navigation ─────────────────────────────────────────────────────────
    _advance() {
      if (!this._validateStep(this.currentStep)) return;
      if (this.currentStep === this._totalSteps) return;
      const next = this.currentStep + 1;
      if (next === this._totalSteps) this._buildReview();
      this._showStep(next, 'forward');
    }

    _retreat() {
      if (this.currentStep <= 1) return;
      this._showStep(this.currentStep - 1, 'backward');
    }

    _showStep(num, direction) {
      const old = this.currentStep;
      this.container.querySelectorAll('.qq-step').forEach(el => {
        el.classList.remove('qq-step--active', 'qq-step--leaving', 'qq-step--back', 'qq-step--back-leaving');
      });

      const leaving = old ? this.container.querySelector(`#qq-step-${old}`) : null;
      const entering = this.container.querySelector(`#qq-step-${num}`);

      if (leaving && direction) {
        leaving.classList.add(direction === 'forward' ? 'qq-step--leaving' : 'qq-step--back-leaving');
        setTimeout(() => leaving.classList.remove('qq-step--leaving', 'qq-step--back-leaving'), 300);
      }

      if (entering) {
        if (direction === null) {
          // Initial render — apply synchronously so container height is correct immediately
          entering.classList.add('qq-step--active');
        } else {
          if (direction === 'backward') entering.classList.add('qq-step--back');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              entering.classList.remove('qq-step--back');
              entering.classList.add('qq-step--active');
            });
          });
          // Focus heading for a11y — only on user navigation, not initial render
          setTimeout(() => {
            const h = entering.querySelector('[id^="qq-heading-"]');
            if (h) { h.setAttribute('tabindex', '-1'); h.focus({ preventScroll: false }); }
          }, 50);
        }
      }

      this.currentStep = num;
      this._updateProgress(num, old);
      this._updateNav(num);
      this._announce(`Step ${num} of ${this._totalSteps}: ${this._steps[num-1]?.label || ''}`);

      // Resize steps container to match active step height
      this._resizeContainer();
    }

    _resizeContainer() {
      const container = this.container.querySelector('#qq-steps-container');
      if (!container) return;
      const active = container.querySelector('.qq-step--active');
      if (active) {
        container.style.height = active.scrollHeight + 'px';
        // After transitions settle, let it auto-size
        setTimeout(() => { container.style.height = 'auto'; }, 320);
      }
    }

    _updateProgress(current, _old) {
      this._steps.forEach((step, i) => {
        const dot = this.container.querySelector(`#qq-prog-${step.num}`);
        if (!dot) return;
        dot.classList.remove('active', 'completed');
        if (step.num < current)  dot.classList.add('completed');
        if (step.num === current) dot.classList.add('active');
        dot.setAttribute('aria-current', step.num === current ? 'step' : 'false');
        // Fill connecting line
        const line = this.container.querySelector(`#qq-line-${step.num}`);
        if (line) line.style.width = step.num < current ? '100%' : '0%';
      });
    }

    _updateNav(stepNum) {
      const back   = this.container.querySelector('#qq-btn-back');
      const next   = this.container.querySelector('#qq-btn-next');
      const submit = this.container.querySelector('#qq-btn-submit');

      const isLast = stepNum === this._totalSteps;
      back.style.visibility  = stepNum === 1 ? 'hidden' : 'visible';
      next.style.display     = isLast ? 'none' : '';
      submit.style.display   = isLast ? '' : 'none';
      this._hideErrorBanner();
    }

    // ── Validation ─────────────────────────────────────────────────────────
    _validateStep(num) {
      const step = this._steps[num - 1];
      if (!step) return true;
      let valid = true;
      const key = step.key;

      if (key === 'contact') valid = this._validateContact();
      else if (key === 'product') valid = this._validateProduct();
      else if (key === 'specs') valid = this._validateSpecs();
      return valid;
    }

    _validateContact() {
      const fields = this.cfg.steps.contact?.fields || {};
      let valid = true;
      let firstError = null;
      for (const [name, cfg] of Object.entries(fields)) {
        if (!cfg.required) continue;
        const el = this.container.querySelector(`#qq-${name}`);
        if (!el) continue;
        const val = el.value.trim();
        let msg = '';
        if (!val) msg = `${cfg.label} is required.`;
        else if (name === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val)) msg = 'Please enter a valid email address.';
        else if (name === 'quantity' && (isNaN(val) || Number(val) <= 0)) msg = 'Please enter a valid quantity.';
        if (msg) { this._showFieldError(name, msg); if (!firstError) firstError = el; valid = false; }
        else this._clearFieldError(name);
      }
      if (firstError) firstError.focus();
      return valid;
    }

    _validateProduct() {
      const s = this.cfg.steps.product;
      if (!s?.required) return true;
      if (this.selectedProducts.length === 0) {
        this._showFieldError('product', 'Please select at least one option.');
        return false;
      }
      this._clearFieldError('product');
      return true;
    }

    _validateSpecs() {
      const s = this.cfg.steps.specs;
      let valid = true;
      let firstError = null;
      if (s?.calculator?.enabled !== false) {
        const w = this.container.querySelector('#qq-width');
        const h = this.container.querySelector('#qq-height');
        if (w && (!w.value || isNaN(w.value) || Number(w.value) <= 0)) {
          this._showFieldError('width', 'Please enter a valid width.'); if (!firstError) firstError = w; valid = false;
        } else if (w) this._clearFieldError('width');
        if (h && (!h.value || isNaN(h.value) || Number(h.value) <= 0)) {
          this._showFieldError('height', 'Please enter a valid height.'); if (!firstError) firstError = h; valid = false;
        } else if (h) this._clearFieldError('height');
      }
      for (const dd of (s?.dropdowns || [])) {
        if (!dd.required) continue;
        const el = this.container.querySelector(`#qq-dd-${dd.id}`);
        if (!el) continue;
        const val = el.value;
        if (!val || val.startsWith('—') || val === '') {
          this._showFieldError(`dd-${dd.id}`, `${dd.label} is required.`);
          if (!firstError) firstError = el;
          valid = false;
        } else this._clearFieldError(`dd-${dd.id}`);
      }
      if (firstError) firstError.focus();
      return valid;
    }

    _showFieldError(name, msg) {
      const errEl = this.container.querySelector(`#qq-err-${name}`);
      const inputEl = this.container.querySelector(`#qq-${name}, #qq-dd-${name}`);
      if (errEl) { errEl.querySelector('span').textContent = msg; errEl.classList.add('qq-visible'); }
      if (inputEl) inputEl.classList.add('qq-input--error', 'qq-select--error');
    }

    _clearFieldError(name) {
      const errEl = this.container.querySelector(`#qq-err-${name}`);
      const inputEl = this.container.querySelector(`#qq-${name}, #qq-dd-${name}`);
      if (errEl) { errEl.classList.remove('qq-visible'); errEl.querySelector('span').textContent = ''; }
      if (inputEl) inputEl.classList.remove('qq-input--error', 'qq-select--error');
    }

    // ── Product card selection ─────────────────────────────────────────────
    _selectCard(card) {
      const id = card.dataset.productId;
      const multi = this.cfg.steps.product?.multiSelect;
      if (!multi) {
        this.selectedProducts = [];
        this.container.querySelectorAll('.qq-option-card').forEach(c => {
          c.classList.remove('qq-selected'); c.setAttribute('aria-checked', 'false');
        });
      }
      const idx = this.selectedProducts.indexOf(id);
      if (idx === -1) {
        this.selectedProducts.push(id);
        card.classList.add('qq-selected');
        card.setAttribute('aria-checked', 'true');
      } else {
        this.selectedProducts.splice(idx, 1);
        card.classList.remove('qq-selected');
        card.setAttribute('aria-checked', 'false');
      }
      this._clearFieldError('product');
    }

    // ── Unit toggle & size calculator ──────────────────────────────────────
    _setUnit(unit) {
      const prev = this.currentUnit;
      if (unit === prev) return;
      this.currentUnit = unit;
      const ratio = unit === 'in' ? 1 / 2.54 : 2.54;

      ['qq-width', 'qq-height'].forEach(id => {
        const el = this.container.querySelector(`#${id}`);
        if (el && el.value && !isNaN(el.value)) {
          el.value = (parseFloat(el.value) * ratio).toFixed(2);
        }
      });

      this.container.querySelectorAll('.qq-unit-btn').forEach(btn => {
        const active = btn.dataset.unit === unit;
        btn.classList.toggle('qq-active', active);
        btn.setAttribute('aria-pressed', active);
      });

      this.container.querySelectorAll('.qq-unit-suffix').forEach(el => el.textContent = unit);
      const unitLabel = this.container.querySelector('#qq-size-unit');
      if (unitLabel) unitLabel.textContent = unit;
      this._computeSize();
    }

    _computeSize() {
      const w = parseFloat(this.container.querySelector('#qq-width')?.value) || 0;
      const h = parseFloat(this.container.querySelector('#qq-height')?.value) || 0;
      const el = this.container.querySelector('#qq-size-val');
      if (el) el.textContent = `${w.toFixed(2)} × ${h.toFixed(2)}`;
    }

    // ── File upload ────────────────────────────────────────────────────────

    // Allowed file types: extension → magic bytes (hex) that must appear at offset 0.
    // Files whose bytes don't match their extension are rejected.
    static get _ALLOWED() {
      return {
        jpg:  { magic: ['ffd8ff'] },
        jpeg: { magic: ['ffd8ff'] },
        png:  { magic: ['89504e47'] },
        gif:  { magic: ['47494638'] },
        webp: { magic: ['52494646'] },                   // RIFF....WEBP
        pdf:  { magic: ['25504446'] },                   // %PDF
        svg:  { magic: null, textCheck: /<svg[\s>]/i, forbidden: /<script[\s>]/i },  // SVG is XML text; script tags disallowed
        zip:  { magic: ['504b0304', '504b0506', '504b0708'] },
        ai:   { magic: ['25504446', '252150532d'] },     // PDF-based or PS
        eps:  { magic: ['252150532d', '25215053'] },     // %!PS
      };
    }

    _handleFiles(fileList) {
      const s = this.cfg.steps.upload;
      const maxMB  = s?.maxSizeMB || 10;
      const maxNum = s?.maxFiles  || 5;
      Array.from(fileList).forEach(f => this._validateAndAdd(f, maxMB, maxNum));
    }

    async _validateAndAdd(file, maxMB, maxNum) {
      if (this.uploadedFiles.length >= maxNum) {
        this._showErrorBanner(`Max ${maxNum} files allowed.`); return;
      }

      // 1. Size check
      if (file.size > maxMB * 1024 * 1024) {
        this._showErrorBanner(`"${this._esc(file.name)}" exceeds ${maxMB} MB.`); return;
      }

      // 2. Sanitise filename — reject null bytes, path traversal, shell chars
      const cleanName = file.name.replace(/[^\w.\- ]/g, '').trim();
      if (!cleanName || /\0/.test(file.name) || file.name !== cleanName.replace(/\s+/g,' ')) {
        this._showErrorBanner(`"${this._esc(file.name)}" has an invalid filename.`); return;
      }

      // 3. Extension whitelist — reject if no extension or not in allowed list
      const parts = cleanName.toLowerCase().split('.');
      if (parts.length < 2) {
        this._showErrorBanner(`"${this._esc(file.name)}" has no file extension.`); return;
      }

      // 4. Double-extension attack: reject anything like shell.php.jpg
      const dangerousExts = /\.(php\d?|phtml|phar|asp|aspx|jsp|jspx|cgi|pl|py|rb|sh|bash|exe|bat|cmd|htaccess|htpasswd|config|env)$/i;
      if (dangerousExts.test(cleanName)) {
        this._showErrorBanner(`"${this._esc(file.name)}" is not an allowed file type.`); return;
      }
      // Check ALL dot-separated parts for dangerous extensions (catches shell.php.jpg)
      if (parts.slice(0, -1).some(p => dangerousExts.test('.' + p))) {
        this._showErrorBanner(`"${this._esc(file.name)}" contains a disallowed extension.`); return;
      }

      const ext = parts[parts.length - 1];
      const rule = QuoteModule._ALLOWED[ext];
      if (!rule) {
        this._showErrorBanner(`"${this._esc(file.name)}" — file type .${ext} is not allowed.`); return;
      }

      // 5. Magic byte check — read first 8 bytes and compare against known signatures
      try {
        const valid = await this._checkMagicBytes(file, rule);
        if (!valid) {
          this._showErrorBanner(`"${this._esc(file.name)}" content doesn't match its extension. File may be tampered.`);
          return;
        }
      } catch (_) {
        this._showErrorBanner(`"${this._esc(file.name)}" could not be read.`); return;
      }

      // All checks passed — store a safe copy with sanitised name
      const safeFile = new File([file], cleanName, { type: file.type });
      this.uploadedFiles.push(safeFile);
      this._renderFileList();
      this._hideErrorBanner();
    }

    _checkMagicBytes(file, rule) {
      return new Promise((resolve, reject) => {
        if (rule.magic === null && rule.textCheck) {
          // SVG / text-based: read as text and check for expected pattern
          const reader = new FileReader();
          reader.onload = e => {
            const text = e.target.result;
            resolve(rule.textCheck.test(text) && !(rule.forbidden?.test(text)));
          };
          reader.onerror = reject;
          reader.readAsText(file.slice(0, 512));
          return;
        }
        const reader = new FileReader();
        reader.onload = e => {
          const bytes = new Uint8Array(e.target.result);
          const hex = Array.from(bytes).map(b => b.toString(16).padStart(2,'0')).join('');
          resolve(rule.magic.some(sig => hex.startsWith(sig)));
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file.slice(0, 8));
      });
    }

    _renderFileList() {
      const list = this.container.querySelector('#qq-file-list');
      if (!list) return;
      list.innerHTML = this.uploadedFiles.map((f, i) => `
        <div class="qq-file-item">
          <div class="qq-file-item-icon">${icons.file}</div>
          <span class="qq-file-item-name" title="${this._esc(f.name)}">${this._esc(f.name)}</span>
          <span class="qq-file-item-size">${(f.size / 1024).toFixed(0)} KB</span>
          <button class="qq-file-remove" type="button" data-file-idx="${i}" aria-label="Remove ${this._esc(f.name)}">${icons.trash}</button>
        </div>`).join('');

      list.querySelectorAll('.qq-file-remove').forEach(btn => {
        btn.addEventListener('click', () => { this.uploadedFiles.splice(+btn.dataset.fileIdx, 1); this._renderFileList(); });
      });
    }

    // ── Review ─────────────────────────────────────────────────────────────
    _buildReview() {
      const wrap = this.container.querySelector('#qq-review-content');
      if (!wrap) return;
      const data = this._collectData();
      const sections = [];

      // Contact
      if (data.contact) {
        const rows = Object.entries(data.contact).map(([k, v]) =>
          `<div class="qq-review-row"><span class="qq-review-key">${this._esc(this._fieldLabel(k))}</span><span class="qq-review-val">${this._esc(v || '—')}</span></div>`
        ).join('');
        sections.push(this._reviewSection('Contact Info', rows, this._stepNum('contact')));
      }

      // Product
      if (data.product !== undefined) {
        const labels = this.selectedProducts.map(id => {
          const opt = (this.cfg.steps.product?.options || []).find(o => o.id === id);
          return opt ? opt.label : id;
        }).join(', ') || '—';
        sections.push(this._reviewSection('Product Type', `<div class="qq-review-row qq-review-row--full"><span class="qq-review-key">Selected</span><span class="qq-review-val">${this._esc(labels)}</span></div>`, this._stepNum('product')));
      }

      // Specs
      if (data.specs) {
        let rows = '';
        if (data.specs.width) rows += `<div class="qq-review-row"><span class="qq-review-key">Width</span><span class="qq-review-val">${this._esc(data.specs.width)} ${this._esc(this.currentUnit)}</span></div>`;
        if (data.specs.height) rows += `<div class="qq-review-row"><span class="qq-review-key">Height</span><span class="qq-review-val">${this._esc(data.specs.height)} ${this._esc(this.currentUnit)}</span></div>`;
        for (const dd of (this.cfg.steps.specs?.dropdowns || [])) {
          const val = data.specs.dropdowns?.[dd.id] || '—';
          rows += `<div class="qq-review-row"><span class="qq-review-key">${this._esc(dd.label)}</span><span class="qq-review-val">${this._esc(val)}</span></div>`;
        }
        if (rows) sections.push(this._reviewSection('Specifications', rows, this._stepNum('specs')));
      }

      // Files & notes
      if (data.upload) {
        let rows = '';
        if (this.uploadedFiles.length) rows += `<div class="qq-review-row qq-review-row--full"><span class="qq-review-key">Files</span><span class="qq-review-val">${this.uploadedFiles.map(f => this._esc(f.name)).join(', ')}</span></div>`;
        if (data.upload.notes) rows += `<div class="qq-review-row qq-review-row--full"><span class="qq-review-key">Notes</span><span class="qq-review-val">${this._esc(data.upload.notes)}</span></div>`;
        if (rows) sections.push(this._reviewSection('Files & Notes', rows, this._stepNum('upload')));
      }

      wrap.innerHTML = sections.join('');

      // Wire edit buttons
      wrap.querySelectorAll('.qq-review-edit').forEach(btn => {
        btn.addEventListener('click', () => this._showStep(+btn.dataset.step, 'backward'));
      });

      // Render Turnstile CAPTCHA. Keep an existing token: the widget is reset
      // below and its callback replaces it, so nulling here only opens a gap.
      if (this._turnstileWidgetId == null) this._turnstileToken = null;
      const captchaWrap = this.container.querySelector('#qq-captcha-wrap');
      if (captchaWrap && this.cfg.captchaSiteKey) this._mountCaptcha(captchaWrap);
    }

    /**
     * Mount the Turnstile widget on the review step.
     *
     * turnstile/v0/api.js is loaded async, so window.turnstile can still be
     * undefined when this runs — routinely so on a phone on mobile data, or
     * whenever a blocker/DNS filter delays challenges.cloudflare.com. The old
     * code tested window.turnstile once and gave up silently: no widget ever
     * appeared, and _submit() then refused forever with 'Please complete the
     * security check' while there was nothing on screen to complete.
     *
     * So: wait for the script rather than sampling it once, re-use the widget
     * across Edit round-trips, and if it genuinely cannot load, say so and
     * offer a retry instead of dead-ending the customer.
     */
    _mountCaptcha(wrap) {
      const self = this;

      // Returning to review via Edit: drop the old widget and render a fresh one.
      // turnstile.reset() here left a blank 65px box and no token (seen live
      // 2026-09-11); a fresh render is the path proven to work. The previous
      // token stays in _turnstileToken until the new callback replaces it.
      if (this._turnstileWidgetId != null && window.turnstile) {
        try { window.turnstile.remove(this._turnstileWidgetId); } catch (e) { /* already gone */ }
        this._turnstileWidgetId = null;
      }

      wrap.innerHTML = '';
      wrap.style.minHeight = '';
      this._captchaFailed = false;
      this._captchaReason = null;
      this._ensureTurnstileScript();

      const STEP = 150;
      const SCRIPT_LIMIT = 20000;   // the script genuinely failing to load
      const LAYOUT_LIMIT = 90000;   // the container simply not on screen yet
      let waited = 0;

      // Render only into a box that has layout: a widget built inside a
      // display:none / zero-size container cannot draw an interactive
      // challenge, so no token ever arrives.
      const isLaidOut = () => {
        // Width and actually-being-rendered is all Turnstile needs; the height
        // comes from the widget itself, so requiring height on a container we
        // just emptied is circular. offsetParent === null covers display:none
        // and any hidden ancestor.
        if (!wrap.isConnected || wrap.offsetParent === null) return false;
        return wrap.getBoundingClientRect().width > 0;
      };

      // These two waits are NOT the same failure and must not share a deadline.
      // "Script missing" is a real failure worth surfacing. "Not laid out yet"
      // is normal — the review step is hidden until the customer reaches it,
      // which can easily take longer than any timeout worth setting. Treating
      // the second as a failure is what put the retry/contact fallback in front
      // of people who had done nothing wrong.
      (function waitForTurnstile() {
        const ready = window.turnstile && typeof window.turnstile.render === 'function';

        if (ready && isLaidOut()) {
          try {
            self._turnstileWidgetId = window.turnstile.render(wrap, {
              sitekey: self.cfg.captchaSiteKey,
              theme: 'dark',
              action: 'quote-form',   // separates this tool from the basket in Turnstile analytics
              callback: (token) => { self._turnstileToken = token; self._captchaReason = null; self._hideErrorBanner(); },
              'expired-callback': () => { self._turnstileToken = null; self._captchaReason = 'expired'; },
              'error-callback': (code) => { self._turnstileToken = null; self._captchaReason = 'error-' + code; self._captchaUnavailable(wrap); },
            });
          } catch (e) {
            self._captchaReason = 'render-threw';
            self._captchaUnavailable(wrap);
          }
          return;
        }

        waited += STEP;
        if (!ready && waited >= SCRIPT_LIMIT) { self._captchaReason = 'script-timeout'; self._captchaUnavailable(wrap); return; }
        // Script is fine, we are just waiting to be shown. Keep watching, and
        // if this somehow runs out, stop quietly — _submit() remounts on
        // demand, by which point the step is definitely visible.
        if (waited >= LAYOUT_LIMIT) {
          // Never silent: this exact exit hid a CSS bug for days.
          self._captchaReason = 'layout-timeout';
          console.warn('[EEQUOTE] captcha container never laid out - check for CSS hiding the empty slot');
          return;
        }
        setTimeout(waitForTurnstile, STEP);
      })();
    }

    /** Add the Turnstile script if the host page never included one. */
    _ensureTurnstileScript() {
      if (window.turnstile) return;
      const SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      if (document.querySelector('script[src^="' + SRC + '"]')) return; // theme tag still in flight
      const s = document.createElement('script');
      s.src = SRC;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    /** Turnstile could not load or errored. Explain it and offer a way forward. */
    _captchaUnavailable(wrap) {
      this._turnstileToken = null;
      this._turnstileWidgetId = null;
      this._captchaFailed = true;
      // Say nothing. Submission does not depend on this any more, so an alarming
      // "could not load / retry / call us" panel would only frighten a customer
      // whose quote is about to go through perfectly well. Collapse the slot.
      wrap.innerHTML = '';
      wrap.style.minHeight = '0';
    }

    _reviewSection(title, rows, stepNum) {
      return `
        <div class="qq-review-section">
          <div class="qq-review-section-header">
            <span class="qq-review-section-title">${title}</span>
            <button class="qq-review-edit" type="button" data-step="${stepNum}" aria-label="Edit ${title}">Edit</button>
          </div>
          <div class="qq-review-body">${rows}</div>
        </div>`;
    }

    // ── Collect data ───────────────────────────────────────────────────────
    _collectData() {
      const data = {};
      const q = id => this.container.querySelector(id);

      // Contact
      const contactFields = this.cfg.steps.contact?.fields || {};
      if (this.cfg.steps.contact?.enabled !== false) {
        data.contact = {};
        for (const name of Object.keys(contactFields)) {
          const el = q(`#qq-${name}`);
          if (el) data.contact[name] = el.value.trim();
        }
      }

      // Honeypot (see _renderStepContact). Sent to the server, which flags it when filled.
      const hp = q('#qq-hp');
      data.hp = hp ? hp.value : '';

      // Product
      if (this.cfg.steps.product?.enabled !== false) data.product = [...this.selectedProducts];

      // Specs
      if (this.cfg.steps.specs?.enabled !== false) {
        data.specs = {};
        const wEl = q('#qq-width'), hEl = q('#qq-height');
        if (wEl) data.specs.width  = wEl.value;
        if (hEl) data.specs.height = hEl.value;
        data.specs.unit = this.currentUnit;
        data.specs.dropdowns = {};
        for (const dd of (this.cfg.steps.specs?.dropdowns || [])) {
          const el = q(`#qq-dd-${dd.id}`);
          if (el) data.specs.dropdowns[dd.id] = el.value;
        }
      }

      // Upload
      if (this.cfg.steps.upload?.enabled !== false) {
        data.upload = {};
        const notesEl = q('#qq-notes');
        if (notesEl) data.upload.notes = notesEl.value.trim();
        data.upload.files = this.uploadedFiles;
      }

      return data;
    }

    // ── Submit ─────────────────────────────────────────────────────────────
    async _submit() {
      const btn = this.container.querySelector('#qq-btn-submit');
      const back = this.container.querySelector('#qq-btn-back');
      this._hideErrorBanner();

      // The bot check never blocks submission.
      //
      // Every previous version gated this on a token, and every version found a
      // new way to strand a real customer: the widget not mounted, mounted into
      // a zero-size box, or — the last one — a failure timer that _submit()
      // itself reset on every press, so anyone tapping the button instead of
      // waiting 20s saw "Loading the security check" forever.
      //
      // The token is best-effort now. Turnstile still runs and still returns one
      // silently in the large majority of cases, and quote-submit verifies it
      // server-side; when it is missing the server saves the quote anyway and
      // flags it for Ben. A lost enquiry is worse than a flagged one.
      btn.classList.add('qq-btn--loading');
      btn.disabled = true;
      back.disabled = true;

      try {
        const data = this._collectData();
        // The server verifies this token when present and records why it is
        // missing when it is not.
        data.turnstileToken = this._turnstileToken;
        data.turnstileClientReason = this._turnstileToken ? null : (this._captchaReason || 'no-token');
        await this.cfg.onSubmit(data);
        btn.classList.remove('qq-btn--loading');
        btn.classList.add('qq-btn--success');
        btn.querySelector('.qq-btn-label').style.display = '';
        btn.querySelector('.qq-btn-label').textContent = 'Submitted!';
        this._announce('Quote submitted successfully!');
        setTimeout(() => this._showSuccessScreen(), 600);
      } catch (err) {
        btn.classList.remove('qq-btn--loading');
        btn.disabled = false;
        back.disabled = false;
        this._showErrorBanner('Something went wrong. Please try again.');
        console.error('[EEQUOTE] Submit error:', err);
      }
    }

    _showSuccessScreen() {
      const card = this.container.querySelector('.qq-card');
      const stepsContainer = this.container.querySelector('#qq-steps-container');
      const nav = this.container.querySelector('#qq-nav');
      const header = this.container.querySelector('.qq-card-header');
      const progress = this.container.querySelector('.qq-progress');
      const success = this.container.querySelector('#qq-success-screen');

      [stepsContainer, nav, header, progress].forEach(el => { if (el) el.style.display = 'none'; });
      if (success) success.classList.add('qq-visible');
    }

    // ── Banners ────────────────────────────────────────────────────────────
    _showErrorBanner(msg) {
      const el = this.container.querySelector('#qq-error-banner');
      const txt = this.container.querySelector('#qq-error-text');
      if (el) { txt.textContent = msg; el.classList.add('qq-visible'); }
    }

    _hideErrorBanner() {
      const el = this.container.querySelector('#qq-error-banner');
      if (el) el.classList.remove('qq-visible');
    }

    // ── A11y live announce ─────────────────────────────────────────────────
    _announce(msg) {
      const el = this.container.querySelector('#qq-live');
      if (!el) return;
      el.textContent = '';
      requestAnimationFrame(() => { el.textContent = msg; });
    }

    // ── Helpers ────────────────────────────────────────────────────────────
    _stepNum(key) {
      return this._steps.find(s => s.key === key)?.num ?? 0;
    }

    _esc(str) {
      if (str == null) return '';
      return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
    }

    _fieldLabel(name) {
      const map = { firstName:'First Name', lastName:'Last Name', email:'Email', phone:'Phone', deadline:'Need by Date', quantity:'Quantity' };
      return map[name] || name;
    }

    _autoComplete(name) {
      const map = { firstName:'given-name', lastName:'family-name', email:'email', phone:'tel', quantity:'off', deadline:'off' };
      return map[name] || 'off';
    }
  }

  // Expose globally
  global.QuoteModule = QuoteModule;

})(typeof window !== 'undefined' ? window : global);
